/* eslint-disable */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { API_URL, ENDPOINTS } from "@/api/application-api";
import { useReportContext, SortConfigItem } from "@/components/Evaluate/report-context";
import {
  Workflow,
  WorkflowEdge,
  WorkflowVersionDetailResponse,
  WorkflowInfo,
  WorkflowNode,
  WorkflowNodesResponse,
  WorkflowVersion,
  WorkflowVersionExecution,
  WorkflowVersionResponse,
} from "@/components/Workflows/types";
import { MAX_SIDEBAR_NEW_REQUESTS_THRESHOLD, queryKeys } from "@/constants";
import { useAuth, useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { ToastType } from "@/enums";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { promptLabel } from "@/schemas";
import {
  BasePromptTemplate,
  CreatePromptTemplate,
  CreatePromptVersion,
  InferenceClient,
  InferenceClientRequestBody,
  ListPromptVersions,
  ListPromptVersionsParams,
  PromptBlueprint,
} from "@/types";
import { PromptTemplateFromRegistry, PromptVersion } from "@/types/apiGetters";
import { DatasetGroupRead, DatasetGroupWrite } from "@/types/dataset-groups";
import { ValidationError } from "@/types/errors";
import { Report, ReportCell, ReportColumn } from "@/types/evaluate";
import { ProcessingType } from "@/components/DatasetEditor";
import { CellStatus } from "@/components/Evaluate/TableComponents/RowCell/Cells/primitives";
import { useActiveOrganization } from "@/hooks/organizations";
import {
  ListPromptRegistryObjects,
  PaginatedPromptRegistryObjects,
  PromptRegistry,
} from "@/types/prompt-registry";
import { ReleaseLabelGroupReadList } from "@/types/release-label-groups";
import { Request, Scores } from "@/types/requests";
import { ErrorResponse } from "@/types/response";
import { Span } from "@/types/spans";
import { WorkspaceMember } from "@/types/workspaceMembers";
import { Workspace } from "@/types/workspaces";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { authHeader, jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { readDateRangeParamValues, URL_PARAM_KEYS } from "@/utils/utils";
import { toJS } from "mobx";
import { IndividualRunResponseI } from "@/queries/types";
import { invalidateFavoritesList } from "@/queries/favorites";
import { useInvalidateFolderEntitiesForEntity } from "@/queries/folder-entities/useInvalidateFolderEntitiesForEntity";

const SPAN_REFETCH_INTERVAL = import.meta.env.REACT_APP_SPAN_REFETCH_INTERVAL
  ? Number(import.meta.env.REACT_APP_SPAN_REFETCH_INTERVAL)
  : 5000;

const fetchRequests = (
  userToken: string,

  searchParams: Record<string, any>,
  pageParam: number,
  workspace_id: number,
  sessionEndTime?: Date | null,
) => {
  const url = new URL(ENDPOINTS.get_requests(workspace_id));
  url.searchParams.set("include_prompt_name", "true");
  url.searchParams.set("page", pageParam.toString());
  if (searchParams.q) url.searchParams.set("q", searchParams.q);

  if (searchParams.per_page) {
    url.searchParams.set("per_page", searchParams.per_page);
  }

  if (searchParams.selectedTags?.length)
    searchParams.selectedTags.map((tag: string) => url.searchParams.append("tags_and", tag));

  if (searchParams.sortBy) {
    url.searchParams.set("sort_by", searchParams.sortBy.id);
    url.searchParams.set("sort_order", searchParams.sortBy.desc ? "desc" : "asc");
  }

  if (searchParams.metadata_and?.length)
    searchParams.metadata_and.map((metadata: string) =>
      url.searchParams.append("metadata_and", JSON.stringify(metadata)),
    );

  if (searchParams.metadata_or?.length)
    searchParams.metadata_or.map((metadata: string) =>
      url.searchParams.append("metadata_or", JSON.stringify(metadata)),
    );

  if (searchParams.tags_and?.length)
    searchParams.tags_and.map((tag: string) => url.searchParams.append("tags_and", tag));

  if (searchParams.tags_or?.length)
    searchParams.tags_or.map((tag: string) => url.searchParams.append("tags_or", tag));

  if (searchParams.starred) url.searchParams.set("starred", searchParams.starred);
  if (searchParams.metadataFields?.length > 0) {
    searchParams.metadataFields.forEach((field: string) => {
      url.searchParams.append("metadata_and", JSON.stringify(field));
    });
  }

  if (searchParams.start_time) {
    url.searchParams.set("start_time", searchParams.start_time.toISOString());

    if (!searchParams.end_time) {
      const endTime = new Date(searchParams.start_time);
      endTime.setDate(endTime.getDate() + 1);
      url.searchParams.set("end_time", endTime.toISOString());
    }
  } else if (searchParams.startTime) url.searchParams.set("start_time", searchParams.startTime);

  if (searchParams.end_time) url.searchParams.set("end_time", searchParams.end_time.toISOString());
  else if (searchParams.endTime) url.searchParams.set("end_time", searchParams.endTime);
  // Override with session end time if it exists - BUT ONLY for pagination (page > 1)
  // This ensures the first page always shows the latest requests
  else if (sessionEndTime && pageParam > 1) {
    url.searchParams.set("end_time", sessionEndTime.toISOString());
  }

  if (searchParams.scores?.length > 0)
    url.searchParams.set("scores", JSON.stringify(searchParams.scores));
  if (searchParams.promptTemplate)
    url.searchParams.set("prompt_template", JSON.stringify(searchParams.promptTemplate));

  if (searchParams.limit) url.searchParams.set("limit", searchParams.limit);

  if (searchParams.prompt_template) {
    url.searchParams.set("prompt_template", JSON.stringify(searchParams.prompt_template));
  }

  if (searchParams.prompt_templates_include?.length) {
    searchParams.prompt_templates_include.forEach(
      (template: { name: string; version_numbers?: number[] }) =>
        url.searchParams.append("prompt_templates_include[]", JSON.stringify(template)),
    );
  }

  if (searchParams.prompt_templates_exclude?.length) {
    searchParams.prompt_templates_exclude.forEach(
      (template: { name: string; version_numbers?: number[] }) =>
        url.searchParams.append("prompt_templates_exclude[]", JSON.stringify(template)),
    );
  }

  if (searchParams.status?.length) {
    searchParams.status.forEach((status: string) => url.searchParams.append("status", status));
  }

  if (searchParams.order_by_random) {
    url.searchParams.set("order_by_random", "true");
  }

  return fetch(url, {
    headers: authHeader(userToken),
    credentials: "include",
  }).then(handleResponse);
};

export const useRequests = (
  userToken: string,

  searchParams: Record<string, any>,
  workspace_id: number,
  enabledFlag = true,
  sessionEndTime?: Date | null,
) =>
  useInfiniteQuery<{
    has_next: boolean;
    has_prev: boolean;
    items: Request[];
    next_num: number | null;
    page: number;
    pages: number;
    per_page: number;
    prev_num: number | null;
    total: number;
  }>(
    [queryKeys.requests, userToken, searchParams, workspace_id, sessionEndTime],
    ({ pageParam = 1 }) =>
      fetchRequests(userToken, searchParams, pageParam, workspace_id, sessionEndTime),
    {
      getNextPageParam: lastPage => {
        if (lastPage.has_next) {
          return lastPage.next_num;
        }
      },
      placeholderData: {
        pageParams: [1],
        pages: [
          {
            has_next: false,
            has_prev: false,
            items: [],
            next_num: null,
            page: 1,
            pages: 1,
            per_page: 100,
            prev_num: null,
            total: 1,
          },
        ],
      },
      enabled: enabledFlag,
    },
  );

export const useDuplicatePromptTemplateToWorkspace = (userToken: string) =>
  useMutation(
    ({
      prompt_template_id,
      workspace_id,
      folder_id,
    }: {
      prompt_template_id: string;
      workspace_id: number;
      folder_id?: number;
    }) =>
      fetch(`${ENDPOINTS.duplicate_prompt_template_to_workspace}`, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_template_id,
          workspace_id,
          ...(folder_id !== undefined && { folder_id }),
        }),
      }).then(handleResponse),
    {
      onError: error => {
        displayErrorToast(error || "Failed to duplicate prompt template");
      },
    },
  );

export const useBranchToWorkspace = (userToken: string) =>
  useMutation(
    ({
      prompt_template_id,
      version_id,
      new_template_name,
      workspace_id,
      existing_template_id,
    }: {
      prompt_template_id: string;
      version_id: number;
      new_template_name: string | null;
      workspace_id: number;
      existing_template_id: number | null;
    }) =>
      fetch(`${ENDPOINTS.branch_prompt_template_version}`, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_template_id,
          workspace_id,
          version_id,
          new_template_name,
          existing_template_id,
        }),
      }).then(handleResponse),
  );

export const useRequestPrompt = (
  promptId: string,
  workspaceId: number,
  version: string,
  userToken: string,
  pageSize: number,
  pageNumber: number,
  sortBy: string,
  sortOrder: string,
  enabled = true,
) => {
  const url = ENDPOINTS.get_requests_for_prompt(workspaceId, promptId, version);
  return useQuery(
    [url, pageSize, pageNumber, sortBy, sortOrder],
    () =>
      fetch(
        `${url}?per_page=${pageSize}&page=${pageNumber}&sortBy=${
          sortBy ? sortBy : "id"
        }&sortOrder=${sortOrder ? sortOrder : "desc"}`,
        {
          headers: authHeader(userToken),
        },
      ).then(handleResponse),
    {
      enabled: enabled,
    },
  );
};

export const useRequestCountsForVersions = (
  promptId: string,
  workspaceId: string,
  userToken: string,
) =>
  useQuery(["get-request-counts-for-versions", promptId, workspaceId], () =>
    fetch(
      `${ENDPOINTS.get_request_counts_for_versions}?prompt_id=${promptId}&workspace_id=${workspaceId}`,
      {
        headers: authHeader(userToken),
      },
    ).then(handleResponse),
  );

export const useEvaluationScoresForVersions = (promptId: string, userToken: string) => {
  const { activeWorkspaceId } = useUser();
  const api = ENDPOINTS.get_prompt_version_scores(activeWorkspaceId!, promptId);
  return useQuery([api], () =>
    fetch(api, {
      headers: authHeader(userToken),
    }).then(handleResponse),
  );
};

export const useGroupRequests = (groupId: string, userToken: string) => {
  const { activeWorkspaceId } = useUser();
  const api = ENDPOINTS.get_group_requests(activeWorkspaceId!, groupId);
  return useQuery([api], () =>
    fetch(api, {
      headers: authHeader(userToken),
    }).then(handleResponse),
  );
};

interface UseAnalyticsParams {
  userToken: string;
  startDate: string;
  endDate: string;
  tags?: string[];
  metadata: { key: string; value: string }[];
  starred?: boolean;
  workspace_id: number | null;
}

export const useAnalytics = ({
  userToken,
  startDate,
  endDate,
  tags,
  metadata,
  starred,
  workspace_id,
}: UseAnalyticsParams) =>
  useQuery(
    ["analytics", startDate, endDate, tags, metadata, starred, workspace_id],
    () => {
      const url = new URL(ENDPOINTS.statistics_page);
      if (startDate) url.searchParams.set("start_date", startDate);
      if (endDate) url.searchParams.set("end_date", endDate);
      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
      }

      if (tags && tags.length) tags.map((tag: string) => url.searchParams.append("tags", tag));
      if (metadata.length > 0) url.searchParams.set("metadata", JSON.stringify(metadata));
      if (starred) url.searchParams.set("starred", "true");

      const res = fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);

      return res;
    },
    {
      placeholderData: {
        success: false,
        averageLatency: 0,
        totalCost: 0,
        totalRequests: 0,
        requestsCount: [],
        tokensCount: [],
        mostUsedModels: [],
        cost: [],
        latency: [],
      },
    },
  );

interface useRequestStatsByPromptParams {
  endDate: string;
  metadata: { key: string; value: string }[];
  page: number;
  perPage: number;
  sortBy?: string;
  sortOrder?: string;
  startDate: string;
  tags: string[];
  userToken: string;
  workspace_id?: number;
  starred?: boolean;
}

export const useRequestStatsByPrompt = ({
  endDate,
  metadata,
  page,
  perPage,
  sortBy = "total_cost",
  sortOrder = "desc",
  startDate,
  tags,
  userToken,
  workspace_id,
  starred,
}: useRequestStatsByPromptParams) => {
  const url = new URL(ENDPOINTS.request_stats_by_prompt);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("per_page", perPage.toString());
  url.searchParams.set("sort_by", sortBy);
  url.searchParams.set("sort_order", sortOrder);
  if (workspace_id) url.searchParams.set("workspace_id", workspace_id.toString());
  if (startDate) url.searchParams.set("start_date", startDate);
  if (endDate) url.searchParams.set("end_date", endDate);
  if (tags) url.searchParams.set("tags", JSON.stringify(tags));
  if (metadata) url.searchParams.set("metadata", JSON.stringify(metadata));
  if (starred !== undefined) url.searchParams.set("starred", starred.toString());

  return useQuery(
    [
      "request_stats_by_prompt",
      endDate,
      metadata,
      page,
      perPage,
      sortBy,
      sortOrder,
      startDate,
      tags,
      workspace_id,
      starred,
    ],
    () =>
      fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      initialData: {
        items: [],
        totalCost: 0,
      },
    },
  );
};

export const useTags = (
  userToken: string,
  workspace_id: number | null,
  page = 1,
  searchQuery?: string,
) =>
  useQuery(
    [ENDPOINTS.tags, userToken, workspace_id, page, searchQuery],
    () => {
      const url = new URL(ENDPOINTS.tags);

      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
        url.searchParams.set("page", String(page));
        if (searchQuery) url.searchParams.set("q", searchQuery);
      }

      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      placeholderData: [],
    },
  );

export const useSharedRequest = (shareHash: string, isEnabled = true) =>
  useQuery(
    [ENDPOINTS.get_shared_request, shareHash],
    async () => {
      const response = await fetch(`${ENDPOINTS.get_shared_request}?share_hash=${shareHash}`);
      if (!response.ok) {
        throw new Error(String(response.status)); // throw the status as an error
      }
      return response.json();
    },
    {
      enabled: isEnabled,
      retry: 1,
    },
  );

export const useSharedRequestBulk = (shareHash: string, isEnabled = true) =>
  useQuery(
    [ENDPOINTS.get_shared_request, shareHash],
    async () => {
      const response = await fetch(`${ENDPOINTS.get_shared_request}?share_hash=${shareHash}`);
      if (!response.ok) {
        throw new Error(String(response.status)); // throw the status as an error
      }
      return response.json();
    },
    {
      enabled: isEnabled,
      retry: 1,
    },
  );

export const useRequest = (requestId: string, isEnabled = true) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  return useQuery(
    [queryKeys.request, requestId],
    () =>
      fetch(`${ENDPOINTS.request_logs}/${requestId}`, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      enabled: isEnabled,
    },
  );
};

/**
 * Can be called imperatively to fetch a request log by request ID
 * in the body of event handlers for websocket messages.
 */
export const useFetchIndividualRunRequest = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";

  return useMutation({
    mutationFn: async (requestId: number): Promise<IndividualRunResponseI> =>
      await fetch(new URL(`${ENDPOINTS.individual_run_requests}/${toJS(requestId)}`).toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse),
  });
};

export const useRequestBulk = (requestId: string, isEnabled = true) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  return useQuery<Request>(
    [queryKeys.requestBulk, requestId],
    () =>
      fetch(`${ENDPOINTS.request_logs_bulk}/${requestId}`, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      enabled: isEnabled,
    },
  );
};

export const useRequestGroups = (requestId: string | undefined, userToken: string) => {
  const { activeWorkspaceId } = useUser();
  const api = ENDPOINTS.get_groups(activeWorkspaceId!, requestId!);
  return useQuery(
    [api, requestId],
    () =>
      fetch(api, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    { enabled: requestId !== undefined },
  );
};

export const useMetadataFields = (userToken: string, workspace_id: number | null, enabled = true) =>
  useQuery(
    [queryKeys.metadataFields, userToken, workspace_id],
    () => {
      const url = new URL(ENDPOINTS.metadata_fields);

      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
      }

      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      placeholderData: {
        success: true,
        metadataFields: [],
      },
      enabled,
    },
  );

export const useScoreNames = (userToken: string, workspace_id: number | null, enabled = true) =>
  useQuery(
    [queryKeys.scoreNames, userToken, workspace_id],
    () => {
      const url = new URL(ENDPOINTS.score_names);

      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
      }

      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      placeholderData: {
        success: true,
        scoreNames: [],
      },
      enabled,
    },
  );

export const useIntercomVerification = (userToken: string) =>
  useQuery(
    [ENDPOINTS.get_intercom_verification, userToken],
    () =>
      fetch(ENDPOINTS.get_intercom_verification, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    { enabled: !!userToken },
  );

export const useCreateIndividualRun = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: { input_variables: any; prompt_blueprint: PromptBlueprint; workspace_id: number }) =>
      fetch(ENDPOINTS.create_individual_run, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.create_individual_run]);
      },
    },
  );
};

export const useWorkspaceStats = (workspace_id: number, userToken: string) =>
  useQuery([ENDPOINTS.get_workspace_stats, workspace_id], () =>
    fetch(ENDPOINTS.get_workspace_stats(workspace_id), {
      method: "GET",
      headers: authHeader(userToken),
    }).then(handleResponse),
  );

export const useReplaceDataset = (userToken: string, workspaceId: number, blueprintId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: { dataset_group_id: number; dataset_version_number?: number }) =>
      fetch(`${ENDPOINTS.legacy_reports}/${blueprintId}/dataset`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onMutate: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_columns, blueprintId]);
        queryClient.invalidateQueries([ENDPOINTS.report_row_ids(blueprintId)]);
        queryClient.invalidateQueries(["changelog", String(workspaceId), blueprintId, "REPORT"]);
        queryClient.invalidateQueries(["global-changelog", String(workspaceId)]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(blueprintId)]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, workspaceId]);
      },
      onSettled: () => {
        queryClient.refetchQueries([ENDPOINTS.report_columns, blueprintId]);
        queryClient.invalidateQueries(["changelog", String(workspaceId), blueprintId, "REPORT"]);
        queryClient.invalidateQueries(["global-changelog", String(workspaceId)]);
        queryClient.invalidateQueries([ENDPOINTS.report_row_ids(blueprintId)]);
        queryClient.refetchQueries([ENDPOINTS.report_cells(blueprintId)]);
        queryClient.refetchQueries([ENDPOINTS.legacy_reports, workspaceId]);
      },
    },
  );
};

export const useSetOrRemoveProviderApiKey = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: { api_key: string | null; provider: string; workspace_id: number }) =>
      fetch(ENDPOINTS.set_or_remove_provider_api_key, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.set_or_remove_provider_api_key]);
      },
    },
  );
};

export const useSetOrRemoveBedrockKeys = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: {
      bedrock_aws_access_key?: string;
      bedrock_aws_secret_key?: string;
      bedrock_aws_region?: string;
      workspace_id: number;
    }) =>
      fetch(ENDPOINTS.set_or_remove_bedrock_keys, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.set_or_remove_bedrock_keys]);
      },
    },
  );
};

export const useSetOrRemoveAzureKeys = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: {
      azure_api_key?: string;
      azure_endpoint?: string;
      azure_api_version?: string;
      workspace_id: number;
    }) =>
      fetch(ENDPOINTS.set_or_remove_azure_keys, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.set_or_remove_azure_keys]);
      },
    },
  );
};

export const useGetProviderApiKeyStatus = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const { workspaceId } = useParams();

  return useQuery([ENDPOINTS.get_provider_api_key_status, workspaceId], () => {
    const url = new URL(ENDPOINTS.get_provider_api_key_status);

    if (workspaceId) {
      url.searchParams.set("workspace_id", workspaceId);
    }

    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });
};

export const useDeletePromptLayerApiKey = (userToken: string, workspace_id: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (promptLayerApiKeyId: number) =>
      fetch(`${ENDPOINTS.get_api_keys}/${promptLayerApiKeyId}?workspace_id=${workspace_id}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.get_api_keys]);
      },
      onError: (error: unknown) => {
        console.error("Error deleting PromptLayer API Key:", error);
        displayErrorToast("Error deleting PromptLayer API Key");
      },
    },
  );
};

export const useRefreshPromptLayerApiKeys = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries([ENDPOINTS.get_api_keys]);
  };
};

export const useUpdateApiKeyName = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, name }: { id: number; name?: string }) =>
      fetch(`${ENDPOINTS.get_api_keys}/${id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ name }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.get_api_keys]);
      },
      onError: (error: unknown) => {
        console.error("Error updating API key name:", error);
        displayErrorToast("Error updating API key name");
      },
    },
  );
};
export const usePromptLabelsDelete = (userToken: string) => {
  const queryClient = useQueryClient();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  return useMutation(
    (body: { promptId: number; versionId: number; id: number }) =>
      fetch(`${ENDPOINTS.legacy_prompt_labels}/${body.id}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(async res => {
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          return data;
        }
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || "Failed to delete label");
      }),
    {
      onSuccess: (data, body) => {
        queryClient.invalidateQueries([ENDPOINTS.legacy_prompt_versions]);
        queryClient.invalidateQueries([
          ENDPOINTS.legacy_prompt_versions,
          ENDPOINTS.prompt_versions(workspaceId),
        ]);
        queryClient.invalidateQueries([ENDPOINTS.prompt_labels(workspaceId)]);
        queryClient.invalidateQueries(["changelog", String(workspaceId), body.promptId, "PROMPT"]);
        queryClient.invalidateQueries(["global-changelog", String(workspaceId)]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
        const successMessage = data?.message || "Label deleted successfully";
        const toastType = data?.approved === false ? ToastType.warning : ToastType.success;
        displayToast(successMessage, toastType);
      },
    },
  );
};

export const usePromptLabelsPatch = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const activeWorkspaceId = useUser().activeWorkspaceId;
  return useMutation(
    async (body: { promptId: number; versionId: number; id: number; name: string }) => {
      const res = await fetch(`${ENDPOINTS.legacy_prompt_labels}/${body.id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_version_id: body.versionId,
        }),
      });

      if (!res.ok) {
        let errorMessage = "Failed to update label";
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          errorMessage = res.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return res.json();
    },
    {
      onSuccess: (_, body) => {
        queryClient.invalidateQueries([
          ENDPOINTS.legacy_prompt_versions,
          ENDPOINTS.prompt_versions(activeWorkspaceId ?? 0),
        ]);
        queryClient.invalidateQueries([ENDPOINTS.prompt_labels(activeWorkspaceId ?? 0)]);
        queryClient.invalidateQueries(["changelog", workspaceId, body.promptId, "PROMPT"]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
      },
    },
  );
};

export const useCreatePromptLabel = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const activeWorkspaceId = useUser().activeWorkspaceId;
  return useMutation(
    (body: { promptId: number; versionId: number; name: string }) =>
      fetch(`${ENDPOINTS.prompts}/${body.promptId}/label`, {
        headers: jsonAuthHeaders(userToken),
        method: "POST",
        body: JSON.stringify({
          name: body.name,
          prompt_version_id: body.versionId,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_, body) => {
        queryClient.invalidateQueries([
          ENDPOINTS.legacy_prompt_versions,
          ENDPOINTS.prompt_versions(activeWorkspaceId ?? 0),
        ]);
        queryClient.invalidateQueries([ENDPOINTS.prompt_labels(activeWorkspaceId ?? 0)]);
        queryClient.invalidateQueries(["changelog", workspaceId, body.promptId, "PROMPT"]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
      },
    },
  );
};

export const useDeletePromptTemplate = (userToken: string) => {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useUser();
  return useMutation(
    (body: { id: number }) =>
      fetch(ENDPOINTS.delete_prompt_template, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_id: body.id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.get_prompt_registry_objects(activeWorkspaceId!)]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
      },
    },
  );
};

export const usePromptLabel = () =>
  useMutation((params: { prompt_id: number; name: string; authToken: string }) =>
    fetch(`${ENDPOINTS.prompts}/${params.prompt_id}/label?name=${params.name}`, {
      method: "GET",
      headers: authHeader(params.authToken),
    }).then(async res => {
      if (res.ok) {
        return (await res.json()) as promptLabel.Response;
      }
    }),
  );

export const usePromptLabelId = (
  prompt_id: number,
  label_name: string,
): UseQueryResult<number | undefined> => {
  const userToken = useAuth()?.userToken ?? "";

  return useQuery<number | undefined>([ENDPOINTS.prompts, prompt_id, "label", label_name], () =>
    fetch(`${ENDPOINTS.prompts}/${prompt_id}/label?name=${label_name}`, {
      method: "GET",
      headers: authHeader(userToken),
    }).then(async res => {
      if (res.ok) {
        return ((await res.json()) as promptLabel.Response).id;
      }
    }),
  );
};

export const getWorkspaces = (userToken: string): Promise<{ workspaces: Workspace[] }> =>
  fetch(ENDPOINTS.legacy_workspaces, {
    headers: authHeader(userToken),
  }).then(handleResponse);

export const useWorkspaces = () => {
  const userToken = useAuth()?.userToken ?? "";
  return useQuery([ENDPOINTS.legacy_workspaces, userToken], () => getWorkspaces(userToken), {
    enabled: !!userToken,
  });
};

export const useCreateWorkspace = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ name, organization_id }: { name: string; organization_id: number }): Promise<Workspace> =>
      fetch(ENDPOINTS.workspaces, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
          organization_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.legacy_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.get_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.workspaces]);
        displayToast("Workspace created successfully", ToastType.success);
      },
    },
  );
};

export const useDeleteWorkspace = ({
  accessibleWorkspaces,
}: {
  accessibleWorkspaces: Workspace[];
}) => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { workspaceId: currentWorkspaceId } = useParams();
  const { data: activeOrganization } = useActiveOrganization();
  return useMutation(
    (workspaceId: number) => {
      if (
        accessibleWorkspaces.some(workspace => workspace.id === workspaceId) &&
        workspaceId === Number(currentWorkspaceId)
      ) {
        const defaultWorkspace = accessibleWorkspaces.find(
          workspace => workspace.is_default && workspace.organization_id === activeOrganization?.id,
        );
        navigate(`/workspace/${defaultWorkspace?.id}/home`);
        displayToast("You were moved to the default workspace", ToastType.success);
      }

      return fetch(`${ENDPOINTS.legacy_workspaces}/${workspaceId}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(res => {
        if (res.ok) return "";
        return res.json();
      });
    },
    {
      onSuccess: data => {
        if (typeof data === "object" && data !== null && "message" in data) {
          displayErrorToast((data as { message: string }).message);
          return;
        }
        queryClient.invalidateQueries([ENDPOINTS.legacy_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.get_workspaces]);
      },
    },
  );
};

export const useWorkspaceMembers = (userToken: string, workspace_id: number | null) =>
  useQuery<{ success: boolean; workspace_members: WorkspaceMember[] }>(
    [ENDPOINTS.legacy_workspace_members, userToken, workspace_id],
    () => {
      if (!workspace_id) {
        return Promise.resolve({
          success: true,
          workspace_members: [],
        });
      }

      const url = new URL(ENDPOINTS.legacy_workspace_members);

      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
      }

      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      placeholderData: { success: true, workspace_members: [] },
    },
  );

export const useDeleteWorkspaceMember = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (workspaceMemberId: number) =>
      fetch(`${ENDPOINTS.legacy_workspace_members}/${workspaceMemberId}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.legacy_workspaces]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_workspace_members]);
        queryClient.invalidateQueries(["workspace-members"]);
        displayToast("Workspace member deleted successfully", ToastType.success);
      },
      onError: error => {
        displayErrorToast(error);
      },
    },
  );
};

export const useUpload = () =>
  useMutation(
    async ({ userToken, file }: { userToken: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(ENDPOINTS.upload, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        body: formData,
      });

      if (!res.ok) {
        // The backend returns JSON ({ message }) for handled errors, but an
        // unhandled 500 renders an HTML page - fall back to a generic message.
        let message = `Failed to upload file (${res.status})`;
        try {
          const body = await res.json();
          if (body?.message) {
            message = body.message;
          }
        } catch {
          // Response body wasn't JSON; keep the default message.
        }
        throw new Error(message);
      }

      const data = (await res.json()) as { success: true; file_url: string };
      if (!data?.file_url) {
        throw new Error("Upload succeeded but no file URL was returned");
      }
      return data;
    },
    {
      onError: error => {
        displayErrorToast(error);
      },
    },
  );

export const useWebhook = (userToken: string, workspace_id: number) =>
  useQuery(
    [ENDPOINTS.webhooks, userToken, workspace_id],
    () => {
      const url = new URL(ENDPOINTS.webhooks);
      url.searchParams.set("workspace_id", workspace_id.toString());
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    { enabled: !!workspace_id },
  );

export const useCreateWebhook = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ url, workspace_id }: { url: string; workspace_id: number }) =>
      fetch(ENDPOINTS.webhooks, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          url,
          workspace_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.webhooks]);
      },
    },
  );
};

export const useDeleteWebhook = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (id: number) =>
      fetch(`${ENDPOINTS.webhooks}/${id}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.webhooks]);
      },
      onError: error => {
        console.error(error);
      },
    },
  );
};

function mapEntityToFolder(
  entity: {
    id: number;
    name: string;
    folder_id: number | null;
    created_at: string;
    updated_at: string;
  },
  workspace_id: number,
): {
  id: number;
  name: string;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  workspace_id: number;
} {
  return {
    id: entity.id,
    name: entity.name,
    parent_id: entity.folder_id,
    created_at: entity.created_at,
    updated_at: entity.updated_at,
    workspace_id,
  };
}

export const useFolders = (userToken: string, workspace_id: number, folderId?: string) =>
  useQuery(
    [
      "folder-entities",
      String(workspace_id),
      folderId ? Number(folderId) : null,
      ["FOLDER"],
      undefined,
      !folderId,
      false,
    ],
    async () => {
      const url = new URL(ENDPOINTS.folder_entities(workspace_id.toString()));
      url.searchParams.set("filter_type", "FOLDER");
      url.searchParams.set("flatten", folderId ? "false" : "true");
      if (folderId) {
        url.searchParams.set("folder_id", folderId);
      }
      const data = await fetch(url.toString(), {
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse);
      const entities = data.entities || [];
      const folders = entities.map(
        (e: {
          id: number;
          name: string;
          folder_id: number | null;
          created_at: string;
          updated_at: string;
        }) => mapEntityToFolder(e, workspace_id),
      );
      return { success: true, folders };
    },
    {
      placeholderData: {
        success: true,
        folders: [] as {
          id: number;
          name: string;
          parent_id: number | null;
          created_at: string;
          updated_at: string;
          workspace_id: number;
        }[],
      },
    },
  );

export interface CreateFolderResponse {
  success: true;
  folder: {
    id: number;
    name: string;
    created_at: string;
    updated_at: string;
    workspace_id: number;
    parent_id: number | null;
  };
}

interface CreateFolderVariables {
  name: string;
  workspace_id: number;
  parent_folder_id?: number;
}

export const useCreateFolder = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation<CreateFolderResponse, unknown, CreateFolderVariables>(
    ({ name, workspace_id, parent_folder_id }) =>
      fetch(ENDPOINTS.folders, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
          workspace_id,
          parent_id: parent_folder_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: ["folder-entities", String(variables.workspace_id)],
        });
      },
    },
  );
};

export const useEditFolder = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation(
    ({ folder_id, name }: { folder_id: number; name: string }) =>
      fetch(`${ENDPOINTS.folders}/${folder_id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
        invalidateFavoritesList(queryClient, workspaceId);
      },
      onError: error => {
        if (typeof error === "string") displayErrorToast(error);
      },
    },
  );
};

export const useMovePromptTemplate = (userToken: string) => {
  const queryClient = useQueryClient();
  const { activeWorkspaceId } = useUser();
  return useMutation(
    ({ folder_id, prompt_template_id }: { folder_id: number | null; prompt_template_id: string }) =>
      fetch(`${ENDPOINTS.move_prompt_template}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          folder_id,
          prompt_template_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.get_prompt_registry_objects(activeWorkspaceId!)]);
      },
    },
  );
};

export const useCreateBlueprint = (userToken: string) => {
  const queryClient = useQueryClient();

  const workspaceId = useUser()?.activeWorkspaceId ?? 0;

  return useMutation(
    ({
      dataset_group_id,
      name,
      folder_id,
      dataset_version_number,
    }: {
      dataset_group_id: number;
      name: string;
      folder_id?: number;
      dataset_version_number?: number;
    }) =>
      fetch(ENDPOINTS.legacy_reports, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          dataset_group_id,
          name,
          folder_id,
          dataset_version_number,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_, v) => {
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports]);
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", String(workspaceId), v.folder_id ?? null],
        });

        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId.toString(), null, undefined, undefined, true],
        });
      },
    },
  );
};

export const useGetBlueprints = (
  userToken: string,
  workspaceId: number,
  page = 1,
  perPage = 100,
  search: string | null = null,
  includeColumns?: boolean,
) =>
  useQuery<{ reports: Report[]; pages: number; total: number }>(
    [ENDPOINTS.get_reports(workspaceId), workspaceId, true, includeColumns, page, perPage, search],
    () => {
      if (workspaceId === null) {
        return Promise.resolve(null);
      }
      const url = new URL(ENDPOINTS.get_reports(workspaceId));
      url.searchParams.set("is_blueprint", "true");
      url.searchParams.set("include_children_report_count", "true");
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", perPage.toString());
      if (search) url.searchParams.set("search", search);
      if (includeColumns) {
        url.searchParams.set("include_columns", "true");
      }
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
  );

export const useReportColumnsIncludePromptTemplateLatestVersion = (
  userToken: string,
  reportId: number,
  promptName: string,
) =>
  useQuery<{ includes_latest_version: boolean; success: boolean }>(
    [
      ENDPOINTS.report_columns_include_prompt_template_latest_version,
      userToken,
      reportId,
      promptName,
    ],
    () => {
      const url = new URL(ENDPOINTS.report_columns_include_prompt_template_latest_version);
      url.searchParams.set("report_id", reportId.toString());
      url.searchParams.set("prompt_name", promptName);
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
  );

export const useGetReports = (
  userToken: string,
  workspaceId: number,
  page = 1,
  perPage = 100,
  search: string | null = null,
  hasPromptVersions: boolean | null = null,
): UseQueryResult<{
  reports: Report[];
  pages: number;
  total: number;
} | null> =>
  useQuery(
    [ENDPOINTS.get_reports(workspaceId), workspaceId, page, perPage, search, hasPromptVersions],
    () => {
      if (workspaceId === null) {
        return Promise.resolve(null);
      }
      const url = new URL(ENDPOINTS.get_reports(workspaceId));
      url.searchParams.set("is_blueprint", "false");
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", perPage.toString());
      if (search) url.searchParams.set("search", search);
      if (hasPromptVersions !== null)
        url.searchParams.set("has_prompt_versions", hasPromptVersions.toString());
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      keepPreviousData: true,
    },
  );

export const useRenameReport = (
  userToken: string,
  reportId: number,
  folder_id: number | null = null,
) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const invalidateFolderEntities = useInvalidateFolderEntitiesForEntity();
  return useMutation((reportName: string) => {
    const url = new URL(`${ENDPOINTS.legacy_reports}/${reportId.toString()}/rename`);
    return fetch(url.toString(), {
      method: "PATCH",
      headers: jsonAuthHeaders(userToken),
      body: JSON.stringify({
        name: reportName,
      }),
    })
      .then(handleResponse)
      .then(json => {
        // on success, update the cache
        if (json.success) {
          invalidateFolderEntities(folder_id);

          queryClient.setQueryData(
            [ENDPOINTS.legacy_reports, reportId],

            (old: any) => ({
              ...old,
              name: reportName,
            }),
          );

          queryClient.invalidateQueries(["changelog", workspaceId, reportId, "REPORT"]);
          queryClient.invalidateQueries(["global-changelog", workspaceId]);
        }

        return json;
      });
  });
};

export const useDeleteReport = (userToken: string, reportId?: number) =>
  useMutation((params: { id?: number; onSuccess?: () => void }) => {
    const idToUse = params.id || reportId;
    if (!idToUse) {
      throw new Error("Report ID must be provided either as parameter or in mutation");
    }
    const url = new URL(`${ENDPOINTS.legacy_reports}/${idToUse.toString()}`);
    return fetch(url.toString(), {
      method: "DELETE",
      headers: authHeader(userToken),
    }).then(() => {
      params.onSuccess?.();
    });
  });

export const useUpdateReportComment = (userToken: string, reportId: number) => {
  const queryClient = useQueryClient();
  return useMutation((comment: string) => {
    const url = new URL(`${ENDPOINTS.legacy_reports}/${reportId.toString()}/comment`);
    return fetch(url.toString(), {
      method: "PATCH",
      headers: jsonAuthHeaders(userToken),
      body: JSON.stringify({
        comment,
      }),
    })
      .then(handleResponse)
      .then(json => {
        // on success, update the cache
        if (json.success) {
          queryClient.setQueryData(
            [ENDPOINTS.legacy_reports, reportId],

            (old: any) => ({
              ...old,
              comment,
            }),
          );
        }

        return json;
      });
  });
};
export const useGetReportColumns = (
  userToken: string,
  reportId: number | null,
  onSuccess?: (data: ReportColumn[] | null) => void,
): UseQueryResult<ReportColumn[] | null> =>
  useQuery<ReportColumn[] | null>(
    [ENDPOINTS.report_columns, reportId],
    () => {
      if (reportId === null) {
        return Promise.resolve(null);
      }
      const url = new URL(ENDPOINTS.report_columns);
      url.searchParams.set("report_id", reportId.toString());
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson.report_columns;
          }
          return null;
        });
    },
    {
      onSuccess: data => {
        onSuccess?.(data);
      },
    },
  );
export const useGetReportColumnStats = (userToken: string, reportColumnId?: number | null) =>
  useQuery([ENDPOINTS.report_columns, reportColumnId, "stats"], () => {
    if (!reportColumnId || reportColumnId === null) {
      return Promise.resolve(null);
    }
    return fetch(`${ENDPOINTS.report_columns}/${reportColumnId}/stats`, {
      headers: authHeader(userToken),
    })
      .then(handleResponse)
      .then(responseJson => {
        if (responseJson?.success) {
          return responseJson.stats;
        }
      });
  });

export const downloadReport = async (userToken: string, reportName: string, reportId: number) => {
  const reportUrl = new URL(`${ENDPOINTS.legacy_reports}/${reportId}/export`);
  const res = await fetch(reportUrl.toString(), {
    headers: {
      "Content-Type": "text/csv",
      Authorization: `Bearer ${userToken}`,
    },
  });
  const blob = await res.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${reportName}_${reportId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const downloadReportMultipart = async (
  userToken: string,
  reportName: string,
  reportId: number,
  setProgress?: (progress: number) => void,
) => {
  let blob = "";
  let offset = undefined;
  let total = 1000;
  do {
    const urlString: string =
      `${ENDPOINTS.legacy_reports}/${reportId}/multipart_export` +
      (offset ? "?offset=" + offset : "");
    const url = new URL(urlString);
    const response = await fetch(url.toString(), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    });
    if (!response.ok) throw new Error("Failed to download dataset");
    const responseData = await response.json();
    blob += responseData["data"];
    offset = responseData["offset"];
    if ("total" in responseData) {
      total = responseData["total"];
    }
    if (setProgress) {
      setProgress((100.0 * offset) / total);
    }
  } while (offset);
  const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${reportName}_${reportId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const useCreateReportColumn = (
  userToken: string,
  reportId: number,
  options?: {
    onSuccess?: (response: any) => void;

    onError?: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (columnData: ReportColumn) => {
      if (columnData.configuration?.template) delete columnData.configuration.template?.id;

      return fetch(ENDPOINTS.report_columns, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(columnData),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson;
          } else {
            return Promise.reject(responseJson);
          }
        });
    },
    {
      onSuccess: response => {
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
        options?.onSuccess?.(response);
      },
      onError: error => {
        options?.onError?.(error);
      },
    },
  );
};

export const useDeleteReportColumn = (userToken: string, reportId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (columnId: number) =>
      fetch(`${ENDPOINTS.report_columns}/${columnId}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
      },
    },
  );
};

export const usePatchReportColumn = (
  userToken: string,
  reportId: number,
  options?: {
    onSuccess?: () => void;

    onError?: (error: any) => void;
  },
) => {
  const queryClient = useQueryClient();

  return useMutation(
    (column: ReportColumn) =>
      fetch(`${ENDPOINTS.report_columns}/${column.id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(column),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson;
          } else {
            return Promise.reject(responseJson);
          }
        }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);
        queryClient.invalidateQueries(["getUpdatedReportCells"]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
        options?.onSuccess?.();
      },
      onError: error => {
        options?.onError?.(error);
      },
    },
  );
};

export const useDuplicateReportColumn = (userToken: string, reportId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (columnId: number) =>
      fetch(`${ENDPOINTS.report_columns}/${columnId}/duplicate`, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId), reportId]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
      },
    },
  );
};

export const usePatchReportCell = (userToken: string, reportId: number, reportCellId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ value }: { value: any }) =>
      fetch(ENDPOINTS.report_cell_cellId(reportCellId), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          value: value,
        }),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson;
          } else {
            return Promise.reject(responseJson);
          }
        }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);
        queryClient.invalidateQueries(["getUpdatedReportCells"]);
      },
    },
  );
};

export const useRetryReportCell = (userToken: string, reportId: number, reportCellId: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    () =>
      fetch(ENDPOINTS.report_cells_retry(reportCellId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
      })
        .then(handleResponse)
        .then(responseJson => {
          if (responseJson?.success) {
            return responseJson;
          } else {
            return Promise.reject(responseJson);
          }
        }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.report_cells(reportId)]);
        queryClient.invalidateQueries(["getUpdatedReportCells"]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
      },
      onError: error => {
        displayErrorToast(error);
      },
    },
  );
};

export const useGetSortedRowIds = (
  userToken: string,
  reportId: number,
  search: string | null,
  sortConfig: SortConfigItem[],
  filters: Record<string, string[]>,
  status?: string[],
  onSuccess?: (data: number[]) => void,
) => {
  const api = ENDPOINTS.report_row_ids(reportId);
  const url = new URL(api);
  if (filters && Object.keys(filters).length > 0) {
    const filterStrings = [];
    for (const filterKey of Object.keys(filters)) {
      for (const filterValue of filters[filterKey]) {
        filterStrings.push(filterKey + "=" + filterValue);
      }
    }
    url.searchParams.append("filter_by", filterStrings.join(","));
  }
  if (search) {
    url.searchParams.append("search", search);
  }
  if (status && status.length > 0) {
    url.searchParams.append("status", status.join(","));
  }
  if (sortConfig.length > 0) {
    const sortString = sortConfig.map(value => value.columnId + "=" + value.direction).join(",");
    url.searchParams.append("sort_by", sortString);
  }
  return useQuery<number[]>(
    [url.toString()],
    () =>
      fetch(url.toString(), {
        headers: authHeader(userToken),
      })
        .then(handleResponse)
        .then(responseJson => responseJson.results),
    {
      onSuccess: data => {
        onSuccess?.(data);
      },
    },
  );
};

export const useGetUpdatedReportCells = (
  userToken: string,
  refresh: number,
  reportId: number | null,
  rowIds: number[] | null,
  updatedAt?: Date,
  onSuccess?: (data: ReportCell[] | null) => void,
) => {
  const api = ENDPOINTS.report_cells(reportId || 0);
  const { reportStatus } = useReportContext();
  const queryClient = useQueryClient();

  return useQuery<ReportCell[]>(
    ["getUpdatedReportCells", api, (rowIds || []).join(","), updatedAt ? "updatedAt" : null],
    () => {
      if (rowIds === null || rowIds.length === 0) {
        return [];
      }
      const url = new URL(api);
      if (updatedAt) {
        url.searchParams.set("updated_at_min", updatedAt.toISOString());
      }
      url.searchParams.set("row_ids", rowIds.join(","));
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      })
        .then(handleResponse)
        .then(responseJson => responseJson.results);
    },
    {
      onSuccess: data => {
        if (
          (reportStatus?.RUNNING || 0) +
            (reportStatus?.QUEUED || 0) +
            (reportStatus?.FAILED || 0) +
            (reportStatus?.COMPLETED || 0) ===
          0
        ) {
          queryClient.invalidateQueries([ENDPOINTS.legacy_reports, reportId]);
        }
        queryClient.invalidateQueries([ENDPOINTS.report_columns, reportId]);
        onSuccess?.(data);
      },
      refetchInterval: refresh,
      staleTime: 500,
    },
  );
};

export const useRefreshCells = (userToken: string, updateCells: (cells: ReportCell[]) => void) =>
  useMutation(
    ({ reportId, cellIds }: { reportId: number; cellIds: number[] }) => {
      const url = new URL(ENDPOINTS.report_cells(reportId || 0));
      url.searchParams.set("ids", cellIds.join(","));
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      })
        .then(handleResponse)
        .then(responseJson => responseJson.results);
    },
    {
      onSuccess: (data: ReportCell[]) => {
        updateCells(data);
      },
    },
  );

export const useCreateFinalReport = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation(
    ({ name, reportId }: { name: string; reportId: string }) =>
      fetch(ENDPOINTS.final_reports(reportId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name: name,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_, body) => {
        queryClient.invalidateQueries(["changelog", workspaceId, body.reportId, "REPORT"]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports]);
      },
    },
  );
};

export const useUpdateRequestScore = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      score,
      scoreName,
      requestId,
      resetScore,
      userToken,
    }: {
      score: number;
      scoreName: string;
      requestId: string;
      resetScore: boolean;
      userToken: string;
    }) =>
      fetch(ENDPOINTS.track_score, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          score: score,
          name: scoreName,
          request_id: requestId,
          reset_score: resetScore,
        }),
      }),
    {
      onMutate: variables => {
        const buildNewScores = (oldRequest: any): Scores => {
          let newScores: Scores = oldRequest && oldRequest?.scores ? oldRequest.scores : [];

          // Remove the score if it exists
          newScores = newScores.filter(
            score => !Object.prototype.hasOwnProperty.call(score, variables.scoreName),
          );

          // Update the score
          if (!variables.resetScore && variables.score !== null && variables.score !== undefined) {
            newScores = [...newScores, { [variables.scoreName]: variables.score }];
          }

          return newScores;
        };

        // Update the query data for the specific request (open to request)
        queryClient.setQueryData(
          [queryKeys.request, variables.requestId],

          (old: any) => {
            if (!old) {
              return undefined;
            } else {
              return {
                ...old,
                scores: buildNewScores(old),
              };
            }
          },
        );
      },
    },
  );
};

export const useToggleStarredRequest = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, newVal, userToken }: { id: string; newVal: boolean; userToken: string }) =>
      fetch(ENDPOINTS.toggle_starred_request, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          request_id: id,
          new_value: newVal,
        }),
      }),
    {
      onMutate: variables => {
        // Correctly update the single request's is_starred status
        queryClient.setQueryData(
          [queryKeys.request, variables.id],

          (old: any) => {
            if (!old) {
              return undefined;
            }
            return {
              ...old,
              is_starred: variables.newVal,
            };
          },
        );

        // Correctly update the is_starred status within the list of requests

        queryClient.setQueriesData([queryKeys.requests], (old: any) => ({
          ...old,

          pages: old.pages.map((page: any) => ({
            ...page,

            items: page.items.map((request: any) => {
              if (request.id === parseInt(variables.id)) {
                return {
                  ...request,
                  is_starred: variables.newVal,
                };
              }
              return request;
            }),
          })),
        }));
      },
    },
  );
};

export const useUpdateRequestTags = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ request_id, new_tags_list }: { request_id: string; new_tags_list: string[] }) =>
      fetch(ENDPOINTS.update_tags, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          request_id,
          new_tags_list,
        }),
      }),
    {
      onMutate: variables => {
        queryClient.setQueryData(
          [queryKeys.request, variables.request_id],

          (old: any) => {
            if (!old) {
              return undefined;
            } else {
              return {
                ...old,
                tags_array: [...variables.new_tags_list],
              };
            }
          },
        );

        queryClient.setQueriesData([queryKeys.requests], (old: any) => {
          if (!old?.pages || !Array.isArray(old.pages)) {
            return old;
          }

          return {
            ...old,

            pages: old.pages.map((page: any) => ({
              ...page,

              items: page.items?.map((request: any) => {
                if (!request || request.id !== parseInt(variables.request_id)) {
                  return request;
                }
                return {
                  ...request,
                  tags_array: [...variables.new_tags_list],
                };
              }),
            })),
          };
        });
      },
    },
  );
};

export const useUpdateRequestMetadata = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ request_id, metadata }: { request_id: string; metadata: Record<string, string> }) =>
      fetch(ENDPOINTS.track_metadata, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
        },
        body: JSON.stringify({
          request_id,
          metadata,
        }),
      }),
    {
      onMutate: variables => {
        const newMetadata = Object.keys(variables.metadata).map(key => ({
          [key]: variables.metadata[key],
        }));
        queryClient.setQueryData(
          [queryKeys.request, variables.request_id],

          (old: any) => {
            if (!old) {
              return undefined;
            } else {
              return {
                ...old,
                metadata: newMetadata,
              };
            }
          },
        );

        queryClient.setQueriesData([queryKeys.requests], (old: any) => {
          if (!old?.pages || !Array.isArray(old.pages)) {
            return old;
          }

          return {
            ...old,

            pages: old.pages.map((page: any) => ({
              ...page,

              items: page.items?.map((request: any) => {
                if (!request || request.id !== parseInt(variables.request_id)) {
                  return request;
                }
                return {
                  ...request,
                  metadata: newMetadata,
                };
              }),
            })),
          };
        });
      },
    },
  );
};

export const useParsePromptTemplateInputVariables = () => {
  const queryClient = useQueryClient();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;
  return useMutation(
    ({ prompt_version, userToken }: { prompt_version: PromptBlueprint; userToken: string }) =>
      fetch(ENDPOINTS.parse_input_variables(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_version: prompt_version,
        }),
      })
        .then(res => res.json())
        .catch(err => Promise.reject(err)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.parse_input_variables]);
      },
    },
  );
};

type ParseInputVariablesResponse =
  | { input_variables: string[]; success: true }
  | { message: ValidationError[] | string; success: false };

export const useParseInputVariables = (
  userToken: string,
  prompt_version?: PromptBlueprint,
  options?: { enabled?: boolean },
) => {
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;

  return useQuery(
    [ENDPOINTS.parse_input_variables, prompt_version],
    (): Promise<ParseInputVariablesResponse> => {
      if (!prompt_version) {
        return Promise.resolve({ input_variables: [], success: true });
      }

      return fetch(ENDPOINTS.parse_input_variables(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_version: prompt_version,
        }),
      })
        .then(res => res.json() as Promise<ParseInputVariablesResponse>)
        .catch(err => Promise.reject(err));
    },
    { keepPreviousData: true, enabled: options?.enabled ?? true },
  );
};

export const useCreatePromptTemplate = (userToken: string, folderId?: number) => {
  const [params] = useSearchParams();
  const { workspaceId } = useParams();
  const queryClient = useQueryClient();
  const folder_id = params.get("folder") ? Number(params.get("folder")) : folderId;

  return useMutation(
    (body: CreatePromptTemplate) =>
      fetch(ENDPOINTS.prompt_templates, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          ...body,
          prompt_template: { ...body.prompt_template, folder_id },
        }),
      }).then(handleResponse),
    {
      onSuccess: data => {
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId!, null, undefined, undefined, true],
        });
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId, folder_id ?? null],
        });
        queryClient.invalidateQueries([
          "changelog",
          workspaceId,
          data?.prompt_template?.id,
          "PROMPT",
        ]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
      },
      onError: error => {
        displayErrorToast(error);
      },
    },
  );
};

export const useCreatePromptVersion = (userToken: string) => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useMutation(
    (body: CreatePromptVersion) =>
      fetch(ENDPOINTS.legacy_prompt_versions, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      })
        .then(res => res.json() as Promise<PromptVersion | ErrorResponse>)
        .catch(err => Promise.reject(err)),
    {
      onSuccess: (data, body) => {
        queryClient.invalidateQueries(["changelog", workspaceId, body.prompt_id, "PROMPT"]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
        queryClient.invalidateQueries({
          queryKey: ["snippet-usage"],
        });
      },
    },
  );
};

export const useEditPromptTemplate = () => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();
  const invalidateFolderEntities = useInvalidateFolderEntitiesForEntity();
  return useMutation(
    (body: {
      userToken: string;
      prompt_id: number;
      activeWorkspaceId: number;
      basePromptTemplate: Partial<BasePromptTemplate>;
    }) =>
      fetch(`${ENDPOINTS.prompt_templates}/${body.prompt_id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(body.userToken),
        body: JSON.stringify(body.basePromptTemplate),
      })
        .then(res => handleResponse(res) as Promise<PromptTemplateFromRegistry | ErrorResponse>)
        .catch(err => Promise.reject(err)),
    {
      onSuccess: (data, variables) => {
        if ("message" in data) displayErrorToast(data.message);
        queryClient.invalidateQueries([
          ENDPOINTS.get_prompt_registry_objects(variables.activeWorkspaceId),
        ]);

        invalidateFolderEntities(variables.basePromptTemplate.folder_id ?? null);

        queryClient.invalidateQueries([
          ENDPOINTS.prompt_registry_objects,
          variables.userToken,
          variables.prompt_id,
        ]);

        queryClient.invalidateQueries(["changelog", workspaceId, variables.prompt_id, "PROMPT"]);
        queryClient.invalidateQueries(["global-changelog", workspaceId]);
      },
    },
  );
};

interface UseRequestStatsByTagFieldParams {
  page: number;
  perPage: number;
  sortBy?: string;
  sortOrder: string;
  userToken: string;
  workspace_id: number;
  startDate?: string;
  endDate?: string;
  tags?: string[];

  metadata?: any;
  starred?: boolean;
}

export const useRequestStatsByTag = ({
  page,
  perPage,
  sortBy = "total_cost",
  sortOrder = "desc",
  userToken,
  workspace_id,
  startDate,
  endDate,
  tags,
  metadata,
  starred,
}: UseRequestStatsByTagFieldParams) => {
  const url = new URL(ENDPOINTS.request_stats_by_tag);

  url.searchParams.set("page", page.toString());
  url.searchParams.set("per_page", perPage.toString());
  url.searchParams.set("sort_by", sortBy);
  url.searchParams.set("sort_order", sortOrder);
  url.searchParams.set("workspace_id", workspace_id.toString());
  if (startDate) url.searchParams.set("start_date", startDate);
  if (endDate) url.searchParams.set("end_date", endDate);
  if (tags) url.searchParams.set("tags", JSON.stringify(tags));
  if (metadata) url.searchParams.set("metadata", JSON.stringify(metadata));
  if (starred !== undefined) url.searchParams.set("starred", starred.toString());

  return useQuery(
    [
      ENDPOINTS.request_stats_by_tag,
      page,
      perPage,
      sortBy,
      sortOrder,
      workspace_id,
      startDate,
      endDate,
      tags,
      metadata,
      starred,
    ],
    () =>
      fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      initialData: {
        items: [],
      },
    },
  );
};

interface UseRequestStatsByMetadataFieldParams {
  metadataFieldName: string;
  page: number;
  perPage: number;
  sortBy?: string;
  sortOrder: string;
  userToken: string;
  workspace_id: number;
  startDate?: string;
  endDate?: string;
  tags?: string[];

  metadata?: any;
  starred?: boolean;
}

export const useRequestStatsByMetadataField = ({
  metadataFieldName,
  page,
  perPage,
  sortBy = "total_cost",
  sortOrder = "desc",
  userToken,
  workspace_id,
  startDate,
  endDate,
  tags,
  metadata,
  starred,
}: UseRequestStatsByMetadataFieldParams) => {
  const url = new URL(ENDPOINTS.request_stats_by_metadata_field);
  url.searchParams.set("metadata_field_name", metadataFieldName);
  url.searchParams.set("page", page.toString());
  url.searchParams.set("per_page", perPage.toString());
  url.searchParams.set("sort_by", sortBy);
  url.searchParams.set("sort_order", sortOrder);
  url.searchParams.set("workspace_id", workspace_id.toString());
  if (startDate) url.searchParams.set("start_date", startDate);
  if (endDate) url.searchParams.set("end_date", endDate);
  if (tags) url.searchParams.set("tags", JSON.stringify(tags));
  if (metadata) url.searchParams.set("metadata", JSON.stringify(metadata));
  if (starred !== undefined) url.searchParams.set("starred", starred.toString());

  return useQuery(
    [
      ENDPOINTS.request_stats_by_metadata_field,
      metadataFieldName,
      page,
      perPage,
      sortBy,
      sortOrder,
      workspace_id,
      startDate,
      endDate,
      tags,
      metadata,
      starred,
    ],
    () =>
      fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse),
    {
      initialData: {
        items: [],
      },
    },
  );
};

export const usePromptLayerApiKeys = (userToken: string, workspace_id: number) =>
  useQuery(
    [ENDPOINTS.get_api_keys, userToken, workspace_id],
    () => {
      const url = new URL(ENDPOINTS.get_api_keys);
      url.searchParams.set("workspace_id", workspace_id.toString());
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    { enabled: !!workspace_id },
  );

export const useUsage = (userToken: string, workspace_id: number) =>
  useQuery([ENDPOINTS.usage, userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.usage);
    url.searchParams.set("workspace_id", workspace_id.toString());
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const fetchDatasetGroup = async (
  userToken: string,
  dataset_group_id: string,
): Promise<DatasetGroupRead & DatasetGroupWrite> => {
  const url = new URL(`${ENDPOINTS.dataset_groups}/${dataset_group_id}`);
  const response = await fetch(url, {
    headers: authHeader(userToken),
  });
  const { dataset_group, success, error } = await response.json();

  if (success) {
    return dataset_group;
  }
  return Promise.reject(error.message);
};

export const useDatasetGroups = (
  userToken: string,
  workspace_id: number,
  min_version_number?: number,
) =>
  useQuery([ENDPOINTS.dataset_groups, userToken, workspace_id, min_version_number], () => {
    const url = new URL(ENDPOINTS.dataset_groups);
    url.searchParams.set("workspace_id", workspace_id.toString());
    if (min_version_number !== undefined) {
      url.searchParams.set("min_version_number", min_version_number.toString());
    }
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useCreateDatasetGroup = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      name,
      workspace_id,
      folder_id,
    }: {
      name?: string;
      workspace_id: number;
      folder_id: number | null;
    }) =>
      fetch(ENDPOINTS.dataset_groups, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          name,
          workspace_id,
          folder_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_, { workspace_id, folder_id }) => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_groups]);
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspace_id.toString(), null, undefined, undefined, true],
        });
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", String(workspace_id), folder_id ?? null],
        });
      },
    },
  );
};

export const useDatasets = (userToken: string, dataset_group_id: string) => {
  const activeWorkspace = useActiveWorkspace();
  return useQuery(
    [ENDPOINTS.get_datasets(activeWorkspace?.id ?? 0), userToken, dataset_group_id],
    () => {
      const url = new URL(ENDPOINTS.get_datasets(activeWorkspace?.id ?? 0));
      url.searchParams.set("dataset_group_id", dataset_group_id);
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      placeholderData: [],
    },
  );
};

export const downloadDataset = async (
  userToken: string,
  datasetName: string,
  datasetId: string,
) => {
  const url = new URL(`${ENDPOINTS.datasets}/${datasetId}/export`);
  const response = await fetch(url.toString(), {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${userToken}`,
    },
  });
  if (!response.ok) throw new Error("Failed to download dataset");

  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `${datasetName}_${datasetId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const useAddRequestLogToDataset = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ dataset_group_id, request_log_id }: { dataset_group_id: number; request_log_id: number }) =>
      fetch(ENDPOINTS.add_request_log_to_dataset, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          dataset_group_id,
          request_log_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.add_request_log_to_dataset]);
      },
    },
  );
};

export type {
  AddTraceToTableRequest as AddTraceToDatasetRequest,
  AddTraceToTableResponse as AddTraceToDatasetResponse,
} from "./smart-tables/useAddTraceToTable";
export { useAddTraceToTable as useAddTraceToDataset } from "./smart-tables/useAddTraceToTable";

export const useCreateDatasetColumn = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ dataset_id, name }: { dataset_id: number; name: string }) =>
      fetch(ENDPOINTS.dataset_columns, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          dataset_id,
          name,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useRenameDatasetColumn = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      dataset_id,
      old_name,
      new_name,
    }: {
      dataset_id: number;
      old_name: string;
      new_name: string;
    }) =>
      fetch(`${ENDPOINTS.dataset_columns}/${dataset_id}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          old_name,
          new_name,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useCreateDatasetRow = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      dataset_id,
      variables,
    }: {
      dataset_id: number;

      variables: Record<string, any>;
    }) =>
      fetch(ENDPOINTS.dataset_rows, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          dataset_id,
          variables,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useEditDatasetRow = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      datasetRowId,
      variables,
    }: {
      datasetRowId: number;

      variables: Record<string, any>;
    }) =>
      fetch(`${ENDPOINTS.dataset_rows}/${datasetRowId}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          variables,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useDeleteDatasetRow = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (datasetRowId: number) =>
      fetch(`${ENDPOINTS.dataset_rows}/${datasetRowId}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const getDraftDatasetInformation = async (
  userToken: string,
  dataset_group_id: string,
): Promise<{
  draft_dataset_exists: boolean;
  draft_dataset_id: string;
  is_draft_dataset_populated: boolean;
  success: boolean;
}> => {
  const url = new URL(ENDPOINTS.draft_dataset_information);
  url.searchParams.set("dataset_group_id", dataset_group_id);
  const response = await fetch(url, {
    headers: authHeader(userToken),
  });
  return await response.json();
};

export const useDatasetRowsFromFilterParams = (userToken: string) => {
  const queryClient = useQueryClient();
  const activeWorkspace = useActiveWorkspace();

  return useMutation(
    ({
      dataset_id,
      start_time,
      end_time,
      limit,
      metadata_and,
      metadata_or,
      tags_or,
      prompt_template,
      prompt_templates_include,
      prompt_templates_exclude,
      order_by_random,
      q,
      scores,
      tags_and,
      starred,
      transpose_metadata_columns,
      include_fields,
    }: {
      dataset_id: number;
      start_time?: Date;
      end_time?: Date;
      limit?: number;
      metadata_and?: { key: string; value: string }[];
      metadata_or?: { key: string; value: string }[];
      tags_or?: string[];
      prompt_template?: { name: string; version_numbers?: number[] };
      prompt_templates_include?: { name: string; version_numbers?: number[] }[];
      prompt_templates_exclude?: { name: string; version_numbers?: number[] }[];
      order_by_random?: boolean;
      q?: string;
      scores?: { name: string; operator: string; value: number }[];
      tags_and?: string[];
      starred?: boolean;
      transpose_metadata_columns?: boolean;
      include_fields?: (
        | "variables"
        | "metadata"
        | "scores"
        | "tags"
        | "prompt"
        | "response"
        | "promptlayer_url"
      )[];
    }) =>
      fetch(ENDPOINTS.dataset_rows_from_filter_params, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          dataset_id,
          start_time,
          end_time,
          limit,
          metadata_and,
          prompt_template,
          prompt_templates_include,
          prompt_templates_exclude,
          order_by_random,
          metadata_or,
          tags_or,
          q,
          scores,
          tags_and,
          starred,
          transpose_metadata_columns,
          include_fields,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.get_datasets(activeWorkspace?.id ?? 0)]);
        queryClient.invalidateQueries([ENDPOINTS.datasets]);
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useDatasetInfo = (userToken: string, datasetId: number | undefined) =>
  useQuery<{
    success: boolean;
    name: string;
    version_number: number;
    dataset_group_id: number;
    total: number;
  } | null>(
    [ENDPOINTS.dataset_info, userToken, datasetId],
    () => {
      const url = `${ENDPOINTS.dataset_info}/${datasetId}`;
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: datasetId !== undefined,
      placeholderData: null,
    },
  );

export const useDatasetColumnToJson = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ column_name, dataset_id }: { column_name: string; dataset_id: number }) =>
      fetch(ENDPOINTS.dataset_column_to_json, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          column_name,
          dataset_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      },
    },
  );
};

export const useAvailableReleaseLabels = (userToken: string, workspace_id: number) =>
  useQuery<{
    available_prompt_label_names: string[];
    success: boolean;
  }>([ENDPOINTS.available_release_labels, userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.available_release_labels);
    url.searchParams.set("workspace_id", workspace_id.toString());
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useAllReleaseLabels = (userToken: string, workspace_id: number) =>
  useQuery<{
    available_prompt_label_names: string[];
    success: boolean;
  }>([ENDPOINTS.available_release_labels, "all", userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.available_release_labels);
    url.searchParams.set("workspace_id", workspace_id.toString());
    url.searchParams.set("include_all", "true");
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useAvailableWorkflowReleaseLabels = (userToken: string, workspace_id: number) =>
  useQuery<{
    available_workflow_label_names: string[];
    success: boolean;
  }>([ENDPOINTS.available_workflow_release_labels, userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.available_workflow_release_labels);
    url.searchParams.set("workspace_id", workspace_id.toString());
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useAllWorkflowReleaseLabels = (userToken: string, workspace_id: number) =>
  useQuery<{
    available_workflow_label_names: string[];
    success: boolean;
  }>([ENDPOINTS.available_workflow_release_labels, "all", userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.available_workflow_release_labels);
    url.searchParams.set("workspace_id", workspace_id.toString());
    url.searchParams.set("include_all", "true");
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useReleaseLabelGroups = (userToken: string, workspace_id: number) =>
  useQuery<{
    release_label_groups: ReleaseLabelGroupReadList[];
    success: boolean;
  }>([ENDPOINTS.release_label_groups, userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.release_label_groups);
    url.searchParams.set("workspace_id", workspace_id.toString());
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const useWorkflowReleaseLabelGroups = (userToken: string, workspace_id: number) =>
  useQuery<{
    workflow_release_label_groups: WorkflowReleaseLabelGroup[];
    success: boolean;
  }>([ENDPOINTS.workflow_release_label_groups, userToken, workspace_id], () => {
    const url = new URL(ENDPOINTS.workflow_release_label_groups);
    url.searchParams.set("workspace_id", workspace_id.toString());
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

interface WorkflowReleaseLabelGroup {
  created_at: string;
  id: number;
  workflow_label_name: string;
  workspace_id: number;
}

export const useCreateReleaseLabelGroup = (userToken: string) => {
  const queryClient = useQueryClient();
  const workspaceId = useUser()?.activeWorkspaceId ?? 0;

  return useMutation(
    ({
      prompt_label_name,
      prompt_registry_ids,
      workspace_id,
      folder_id,
    }: {
      prompt_label_name: string;
      prompt_registry_ids: number[];
      workspace_id: number;
      folder_id: number | null;
    }) =>
      fetch(ENDPOINTS.release_label_groups, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_label_name,
          prompt_registry_ids,
          workspace_id,
          folder_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: (_, v) => {
        queryClient.invalidateQueries([ENDPOINTS.release_label_groups]);
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", String(workspaceId), v.folder_id ?? null],
        });
        queryClient.invalidateQueries({
          queryKey: ["folder-entities", workspaceId.toString(), null, undefined, undefined, true],
        });
      },
    },
  );
};

export const useCreateWorkflowReleaseLabelGroup = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      workflow_label_name,
      workflow_ids,
      workspace_id,
    }: {
      workflow_label_name: string;
      workflow_ids: number[];
      workspace_id: number;
    }) =>
      fetch(ENDPOINTS.workflow_release_label_groups, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          workflow_label_name,
          workflow_ids,
          workspace_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.workflow_release_label_groups]);
      },
    },
  );
};

export const useDeleteReleaseLabelGroup = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (releaseLabelGroupId: number) =>
      fetch(`${ENDPOINTS.release_label_groups}/${releaseLabelGroupId}`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.available_release_labels]);
        queryClient.invalidateQueries([ENDPOINTS.release_label_groups]);
      },
    },
  );
};

export const usePromptRegistryObjects = (
  userToken: string,
  params: ListPromptRegistryObjects,
  size?: number,
) => {
  const api = ENDPOINTS.get_prompt_registry_objects(params.workspaceId);
  return useInfiniteQuery<PaginatedPromptRegistryObjects>(
    [api, userToken, params],
    ({ pageParam = 1 }) => {
      const url = new URL(api);
      url.searchParams.set("page", pageParam.toString());
      if (params.promptLabelName) url.searchParams.set("prompt_label_name", params.promptLabelName);
      if (params.perPage) url.searchParams.set("per_page", String(size ?? params.perPage));
      if (params.q) url.searchParams.set("q", params.q);
      if (params.folderId) url.searchParams.set("folder_id", params.folderId);
      if (params.tags?.length) params.tags.forEach(tag => url.searchParams.append("tags", tag));
      if (params.templateType) url.searchParams.set("template_type", params.templateType);
      if (params.searchFolders) url.searchParams.set("search_folders", "True");
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      getNextPageParam: lastPage => {
        if (lastPage.has_next) {
          return lastPage.next_num;
        }
      },
    },
  );
};

export const useDynamicReleaseLabelsByPromptRegistry = (
  userToken: string,
  promptRegistryId: string,
) => {
  const { activeWorkspaceId } = useUser();
  const url = ENDPOINTS.get_dynamic_release_labels_for_prompt(activeWorkspaceId!, promptRegistryId);
  return useQuery<{
    success: boolean;
    dynamic_release_labels: Record<
      number,
      {
        prompt_label_name: string;
        release_label_group_id: number;
      }[]
    >;
  }>([url, userToken], () =>
    fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse),
  );
};

export const useWorkspaceTags = (userToken: string, params: { workspaceId: number; q?: string }) =>
  useQuery<string[]>([ENDPOINTS.workspace_tags(params.workspaceId), userToken, params], () => {
    const url = new URL(ENDPOINTS.workspace_tags(params.workspaceId));
    if (params.q?.trim()) {
      url.searchParams.set("q", params.q.trim());
    }
    return fetch(url, {
      headers: authHeader(userToken),
    }).then(handleResponse);
  });

export const usePromptVersions = (userToken: string, params: ListPromptVersionsParams) => {
  const { activeWorkspaceId } = useUser();
  const url = new URL(ENDPOINTS.prompt_versions(activeWorkspaceId!));
  return useInfiniteQuery<ListPromptVersions>(
    [url.toString(), userToken, JSON.stringify(params)],
    ({ pageParam = 1 }) => {
      url.searchParams.set("workspace_id", params.workspaceId.toString());
      if (params.promptRegistryId)
        url.searchParams.set("prompt_registry_id", params.promptRegistryId.toString());
      url.searchParams.set("page", params.page ?? pageParam.toString());
      if (params.perPage) url.searchParams.set("per_page", params.perPage.toString());
      if (params.label) url.searchParams.set("label", params.label);
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: (params?.promptRegistryId || -1) > 0,
      keepPreviousData: false,
      getNextPageParam: lastPage => (lastPage.has_next ? lastPage.next_num : undefined),
    },
  );
};
export const usePromptRegistry = (userToken: string, id?: number, shouldNavigate = true) =>
  useQuery<PromptRegistry>(
    [ENDPOINTS.prompt_registry_objects, userToken, id],
    () => {
      const url = `${ENDPOINTS.prompt_registry_objects}/${id}`;
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!id,
      retry: 0,
      onError: () => {
        if (!shouldNavigate) return;
      },
    },
  );

export const usePromptVersion = (
  userToken: string,
  promptRegistryId: number | null,
  promptVersionNumber: number | null,
  enabled?: boolean,
) =>
  useQuery<PromptVersion>(
    [ENDPOINTS.legacy_prompt_versions, userToken, promptRegistryId, promptVersionNumber],
    () => {
      const url = `${ENDPOINTS.prompt_registry_objects}/${promptRegistryId}/prompt-versions/${promptVersionNumber}`;
      return fetch(url, {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: enabled || (!!promptRegistryId && !!promptVersionNumber),
      retry: 0,
      onError: () => {
        // navigate("/not-found", { replace: true });
      },
    },
  );

export const usePrompt = (
  userToken: string,
  body: {
    workspace_id: number;
    prompt_name?: string;
    number?: number;
    label?: string;
  },
) =>
  useQuery<PromptVersion>(
    [ENDPOINTS.get_prompt, body],
    () =>
      fetch(ENDPOINTS.get_prompt, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      enabled: !!body.workspace_id && !!body.prompt_name,
      keepPreviousData: true,
    },
  );

export const useCreateBacktest = (userToken: string) =>
  useMutation((body: { promptVersionId: number; workspaceId: number }) =>
    fetch(ENDPOINTS.backtests, {
      method: "POST",
      headers: jsonAuthHeaders(userToken),
      body: JSON.stringify({
        prompt_version_id: body.promptVersionId,
        workspace_id: body.workspaceId,
      }),
    }).then(handleResponse),
  );

export const useCreateBacktestWithLogs = (userToken: string) =>
  useMutation(
    (body: {
      promptVersionId: number;
      workspaceId: number;
      promptVersionIdForLogs?: number | null;
    }) =>
      fetch(ENDPOINTS.backtests, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_version_id: body.promptVersionId,
          workspace_id: body.workspaceId,
          ...(body.promptVersionIdForLogs && {
            prompt_version_id_for_logs: body.promptVersionIdForLogs,
          }),
        }),
      }).then(handleResponse),
  );

export const useCreateABtest = (userToken: string) => {
  const client = useQueryClient();
  return useMutation(
    (body: {
      promptVersionId: number;
      promptId: number;
      labelId?: number | null;
      workspaceId: number;
    }) =>
      fetch(ENDPOINTS.ab_tests, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          prompt_version_id: body.promptVersionId,
          prompt_id: body.promptId,
          label_id: body.labelId,
          workspace_id: body.workspaceId,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        client.invalidateQueries([ENDPOINTS.legacy_prompt_versions]);
      },
    },
  );
};

export const useCheckDynamicReleaseLabel = (
  releaseLabelName: string,
  promptTemplateName: string,
  workspaceId: number,
  userToken: string,
) =>
  useQuery<{ exists: boolean; success: boolean }>(
    [
      ENDPOINTS.verify_dynamic_release_label_existence,
      releaseLabelName,
      promptTemplateName,
      workspaceId,
    ],
    () => {
      const url = new URL(ENDPOINTS.verify_dynamic_release_label_existence);
      url.searchParams.set("release_label_name", releaseLabelName);
      url.searchParams.set("prompt_template_name", promptTemplateName);
      url.searchParams.set("workspace_id", String(workspaceId));

      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
  );

export const useMultipartUpload = (userToken: string, workspaceId: number) =>
  useMutation(
    async (body: {
      fileName?: string | null;
      uploadId?: string | null;
      partNumber?: number | null;
      part?: string | null;
      parts?: { ETag: unknown; PartNumber: number }[] | null;
      datasetId: number;
    }) =>
      fetch(ENDPOINTS.file_upload_multipart, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          file_name: body.fileName,
          upload_id: body.uploadId,
          part_number: body.partNumber,
          part: body.part,
          parts: body.parts,
          dataset_id: body.datasetId,
          workspace_id: workspaceId,
        }),
      }).then(handleResponse),
    {
      onError: error => {
        displayErrorToast(error);
      },
    },
  );

export const useSpanFeed = ({
  userToken,
  searchParams,
  workspace_id,
  parent_id,
  page = 1,
  per_page = 20,
  enabledFlag = true,
}: {
  userToken: string;

  searchParams?: Record<string, any>;
  workspace_id: number;
  parent_id?: string | "none";
  page?: number;
  per_page?: number;
  enabledFlag?: boolean;
}) =>
  useInfiniteQuery<
    {
      success: boolean;
      spans: Span[];
      total: number;
      page: number;
      per_page: number;
      total_pages: number;
      has_next: boolean;
      next_num: number;
    },
    Error
  >(
    [
      ENDPOINTS.spans,
      workspace_id,
      ...Object.values(searchParams || {}),
      parent_id,
      page,
      per_page,
    ],
    async ({ pageParam = 1 }) => {
      const url = new URL(ENDPOINTS.spans);
      url.searchParams.set("workspace_id", workspace_id.toString());
      url.searchParams.set("page", pageParam.toString());
      url.searchParams.set("per_page", per_page.toString());

      if (searchParams?.metadataFields?.length > 0) {
        searchParams?.metadataFields.forEach((field: string) => {
          url.searchParams.append("metadata_and", JSON.stringify(field));
        });
      }

      if (searchParams?.metadata_and?.length > 0) {
        searchParams?.metadata_and?.forEach((field: { key: string; value: string }) => {
          url.searchParams.append("metadata_and", JSON.stringify(field));
        });
      }

      if (searchParams?.metadata_or?.length > 0) {
        searchParams?.metadata_or?.forEach((field: { key: string; value: string }) => {
          url.searchParams.append("metadata_or", JSON.stringify(field));
        });
      }

      if (searchParams?.resource_and?.length > 0) {
        searchParams?.resource_and?.forEach((field: { key: string; value: string }) => {
          url.searchParams.append("resource_and", JSON.stringify(field));
        });
      }

      if (searchParams?.resource_or?.length > 0) {
        searchParams?.resource_or?.forEach((field: { key: string; value: string }) => {
          url.searchParams.append("resource_or", JSON.stringify(field));
        });
      }

      if (searchParams?.trace_id) {
        url.searchParams.set("trace_id", searchParams.trace_id);
      }

      if (searchParams?.start_time) {
        url.searchParams.set("start_time", searchParams.start_time);
      }

      if (searchParams?.end_time) {
        url.searchParams.set("end_time", searchParams.end_time);
      }

      if (parent_id !== undefined) {
        url.searchParams.set("parent_id", parent_id);
      }

      const response = await fetch(url.toString(), {
        headers: authHeader(userToken),
        credentials: "include",
      });

      return handleResponse(response);
    },
    {
      getNextPageParam: lastPage => {
        if (lastPage.has_next) return lastPage.next_num;
      },
      enabled: enabledFlag,
      refetchInterval: query => {
        if (query?.pages.some(p => p.spans.some(span => !span.end))) return SPAN_REFETCH_INTERVAL;

        return false;
      },
    },
  );

export interface TraceIOPreview {
  span_id: string;
  preview: string;
}

export interface TraceEntity {
  kind: "prompt";
  id: number;
  name: string;
  version?: number | null;
}

export const useTraceSpans = ({
  userToken,
  traceId,
  workspace_id,
}: {
  userToken: string;
  traceId: string;
  workspace_id?: number;
}) =>
  useQuery<
    {
      success: boolean;
      trace_id: string;
      spans: Span[];
      trace_input?: TraceIOPreview | null;
      trace_final_output?: TraceIOPreview | null;
      trace_entities?: TraceEntity[];
      logged_before_retention_policy?: boolean;
    },
    Error
  >(
    [ENDPOINTS.trace, traceId, workspace_id],
    async () => {
      const url = new URL(`${ENDPOINTS.trace}/${traceId}`);

      if (workspace_id) {
        url.searchParams.set("workspace_id", workspace_id.toString());
      }

      const response = await fetch(url.toString(), {
        headers: authHeader(userToken),
      });

      return handleResponse(response);
    },
    {
      refetchInterval: query => {
        if (query?.spans && !query.spans.at(0)?.end) return SPAN_REFETCH_INTERVAL;
        return false;
      },
    },
  );

export const useDeleteDatasetRows = (
  userToken: string,
  setProcessingType: (type: ProcessingType | null) => void,
) => {
  const queryClient = useQueryClient();
  const { datasetId } = useParams();

  return useMutation(
    () => {
      setProcessingType("deleteRows" as ProcessingType);

      return fetch(`${ENDPOINTS.datasets}/${datasetId}/dataset-rows`, {
        method: "DELETE",
        headers: authHeader(userToken),
      }).then(() => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.dataset_rows]);
        queryClient.invalidateQueries([ENDPOINTS.datasets]);
      },
      onError: () => {
        setProcessingType(null);
      },
    },
  );
};
export const useWorkflowVersionDetail = (
  workflowVersionId: number,
  workspaceId: number,
  userToken: string,
) =>
  useQuery<WorkflowVersionDetailResponse>(
    [ENDPOINTS.workflow_version_detail(workspaceId), workflowVersionId],
    () => {
      const url = new URL(ENDPOINTS.workflow_version_detail(workspaceId));

      url.searchParams.set("workflow_version_id", String(workflowVersionId));
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!workflowVersionId && !!workspaceId && !!userToken,
      keepPreviousData: true,
    },
  );

export const useWorkflowNodes = (workflowVersionId: number, userToken: string) =>
  useQuery<WorkflowNodesResponse>(
    [ENDPOINTS.workflow_nodes, workflowVersionId],
    () => {
      const url = new URL(ENDPOINTS.workflow_nodes);

      url.searchParams.set("workflow_version_id", String(workflowVersionId));
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!workflowVersionId && !!userToken,
      keepPreviousData: true,
    },
  );

export const useWorkflowVersions = (workflowId: string | undefined, userToken: string) =>
  useQuery<WorkflowVersionResponse>(
    [ENDPOINTS.workflow_versions, workflowId],
    () => {
      const url = new URL(ENDPOINTS.workflow_versions);
      url.searchParams.set("workflow_id", workflowId!);
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!workflowId && !!userToken,
      keepPreviousData: true,
    },
  );

export const useWorkflowDetails = (workflowId: string | undefined, userToken: string) =>
  useQuery<{ success: boolean; workflow: Workflow }>(
    [ENDPOINTS.workflows, workflowId, userToken],
    () => {
      const url = new URL(`${ENDPOINTS.workflows}/${workflowId}`);
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    { enabled: !!workflowId && !!userToken },
  );

export const useWorkflows = (
  workspaceId: string,
  userToken: string,
  workflow_label_name?: string,
  page?: number,
  query?: string,
) =>
  useInfiniteQuery<
    {
      success: boolean;
      workflows: WorkflowInfo[];
      page: number;
      pages: number;
      per_page: number;
      total: number;
    },
    Error
  >(
    [ENDPOINTS.workflows, workspaceId, userToken, workflow_label_name, page, query],
    ({ pageParam = page || 1 }) => {
      const url = new URL(ENDPOINTS.workflows);
      url.searchParams.set("page", pageParam.toString());
      url.searchParams.set("per_page", "20");
      url.searchParams.set("workspace_id", workspaceId);
      if (workflow_label_name) {
        url.searchParams.set("workflow_label_name", workflow_label_name);
      }
      if (query) {
        url.searchParams.set("query", query);
      }
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      getNextPageParam: lastPage => {
        const currentPage = lastPage.page;
        const totalPages = lastPage.pages;
        if (currentPage < totalPages) {
          return currentPage + 1;
        }
        return undefined;
      },
    },
  );

export const useWorkflowVersionExecutions = (
  workflowVersionId: number | undefined,
  workspaceId: number,
  userToken: string,
  page: number,
  perPage: number,
) => {
  const result = useQuery<{
    success: boolean;
    workflow_version_executions: WorkflowVersionExecution[];
    pages: number;
    page: number;
    per_page: number;
    total: number;
    has_next: boolean;
    has_prev: boolean;
    next_num: number | null;
    prev_num: number | null;
  }>(
    [
      ENDPOINTS.workflow_version_executions,
      workflowVersionId,
      workspaceId,
      userToken,
      page,
      perPage,
    ],
    () => {
      const url = new URL(ENDPOINTS.workflow_version_executions);
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", perPage.toString());
      url.searchParams.set("workspace_id", workspaceId.toString());
      url.searchParams.set("workflow_version_id", String(workflowVersionId));
      return fetch(url.toString(), {
        headers: authHeader(userToken),
      }).then(handleResponse);
    },
    {
      enabled: !!workflowVersionId && !!userToken,
      keepPreviousData: true,
    },
  );

  return {
    ...result,
    hasNextPage: result.data?.has_next ?? false,
    hasPrevPage: result.data?.has_prev ?? false,
  };
};

export const useDeleteWorkflowNode = (userToken: string) =>
  useMutation((workflowNodeId: string) =>
    fetch(`${ENDPOINTS.workflow_nodes}/${workflowNodeId}`, {
      method: "DELETE",
      headers: authHeader(userToken),
    }).then(handleResponse),
  );

export const useWorkflowEditorState = (workspaceId?: number, userToken?: string) =>
  useQuery<{
    sucess: boolean;
    workflow: Workflow;
    workflow_nodes: WorkflowNode[];
    workflow_version: WorkflowVersion;
    workflow_version_execution: WorkflowVersionExecution;
  }>(
    [ENDPOINTS.workflow_editor_state, workspaceId],
    () => {
      const url = new URL(ENDPOINTS.workflow_editor_state);
      url.searchParams.set("workspace_id", String(workspaceId));
      return fetch(url.toString(), {
        headers: authHeader(userToken!),
      }).then(handleResponse);
    },
    {
      enabled: !!workspaceId && !!userToken,
      keepPreviousData: false,
    },
  );

export const useWorkflowVersionEditorState = (workflow_version_id?: number, userToken?: string) =>
  useQuery<{
    success: boolean;
    workflow: Workflow;
    edges: WorkflowEdge[];
    workflow_nodes: WorkflowNode[];
    workflow_version: WorkflowVersion;
    workflow_version_execution: WorkflowVersionExecution;
  }>(
    [ENDPOINTS.workflow_editor_state, workflow_version_id],
    async () => {
      if (!workflow_version_id) {
        throw new Error("Workflow version ID is required");
      }
      const url = new URL(ENDPOINTS.workflow_editor_state);
      url.searchParams.set("workflow_version_id", String(workflow_version_id));
      const response = await fetch(url.toString(), {
        headers: authHeader(userToken!),
      });
      if (!response.ok) {
        throw new Error("Failed to fetch workflow editor state");
      }
      const data = await handleResponse(response);
      if (!data.success) {
        throw new Error(data.message || "Failed to fetch workflow editor state");
      }
      return data;
    },
    {
      enabled: !!workflow_version_id && !!userToken,
      keepPreviousData: false,
      retry: 3,
      retryDelay: 1000,
    },
  );

export const useEditInputVariables = (userToken: string, workspace_id: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ input_variables }: { input_variables: Record<string, string> }) =>
      fetch(`${ENDPOINTS.workflow_editor_input_variables}`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          input_variables,
          workspace_id,
        }),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.workflow_versions]);
      },
    },
  );
};

export const useCreateWorkflowLabel = (userToken: string) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: { name: string; workflow_id: number; workflow_version_id: number }) =>
      fetch(ENDPOINTS.workflow_labels, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.workflow_labels]);
      },
    },
  );
};

export const useInferenceClientsList = (
  userToken: string,
  workspaceId: string,
): UseQueryResult<{
  inference_clients: InferenceClient[];
  success: boolean;
}> =>
  useQuery<{
    inference_clients: InferenceClient[];
    success: boolean;
  }>(
    [ENDPOINTS.inference_clients, userToken],
    async () => {
      const url = new URL(ENDPOINTS.inference_clients);
      url.searchParams.set("workspace_id", workspaceId);
      const response = await fetch(url.toString(), {
        headers: authHeader(userToken),
      });
      return handleResponse(response);
    },
    {
      enabled: !!workspaceId,
    },
  );

export const useInferenceClients = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: InferenceClientRequestBody) =>
      fetch(ENDPOINTS.inference_clients, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.inference_clients]);
      },
    },
  );
};

export const useDeleteInferenceClient = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: { id: number }) =>
      fetch(`${ENDPOINTS.inference_clients}/${body.id}`, {
        method: "DELETE",
        headers: authHeader(userToken),
        body: JSON.stringify(body),
      }).then(() => undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.inference_clients]);
      },
    },
  );
};

export const useRequestLogs = (
  filterParams: Record<string, unknown>,
  enabledFlag = true,
  sessionEndTime?: Date | null,
) => {
  const queryClient = useQueryClient();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const user = useUser();
  const activeWorkspaceId = user?.activeWorkspaceId;

  const requestsQuery = useRequests(
    userToken,
    filterParams as Record<string, unknown>,
    activeWorkspaceId!,
    enabledFlag,
    sessionEndTime,
  );

  requestsQuery.data?.pages?.forEach(page => {
    page.items?.forEach((item: any) => {
      queryClient.setQueryData([queryKeys.request, String(item.id)], item);
    });
  });

  return requestsQuery;
};

export const useCreateDatasetFromEvaluationReport = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (body: { dataset_group_id: number; report_id: number }) =>
      fetch(ENDPOINTS.create_dataset_from_evaluation, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(body),
      }).then(handleResponse),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([ENDPOINTS.inference_clients]);
      },
    },
  );
};

export const useSpansQuery = (enabledFlag = true) => {
  const auth = useAuth();
  const user = useUser();
  const userToken = auth?.userToken ?? "";
  const activeWorkspaceId = user?.activeWorkspaceId;
  const [searchParams] = useSearchParams();
  const q = searchParams.get(URL_PARAM_KEYS.TRACE_Q) ?? "";

  // Read filter params directly from URL so the query is reactive to URL changes
  const metadataParams = searchParams.getAll(URL_PARAM_KEYS.METADATA);
  const resourceParams = searchParams.getAll(URL_PARAM_KEYS.RESOURCE);
  const { startTimeParam, endTimeParam } = readDateRangeParamValues(searchParams, [
    "traces",
    "default",
  ]);

  // Each URL entry is `{key, values: [...], logic: "AND"|"OR"}` (older entries may have
  // a single `value` string instead — handled for back-compat). Distribute into _and / _or
  // buckets so the backend's AND-of-conditions vs OR-of-conditions semantics are preserved.
  const splitByLogic = (
    params: string[],
  ): { and: { key: string; value: string }[]; or: { key: string; value: string }[] } => {
    const and: { key: string; value: string }[] = [];
    const or: { key: string; value: string }[] = [];
    params.forEach(param => {
      try {
        const parsed = JSON.parse(param) as {
          key: string;
          values?: (string | number | boolean)[];
          value?: string | number | boolean;
          logic?: "AND" | "OR";
        };
        const valueList = parsed.values ?? (parsed.value !== undefined ? [parsed.value] : []);
        const bucket = parsed.logic === "AND" ? and : or;
        valueList.forEach(v => bucket.push({ key: parsed.key, value: String(v) }));
      } catch {
        // ignore malformed params
      }
    });
    return { and, or };
  };

  const metadataBuckets = splitByLogic(metadataParams);
  const resourceBuckets = splitByLogic(resourceParams);

  const spanSearchParams: Record<string, any> = { trace_id: q };

  if (metadataBuckets.and.length > 0) {
    spanSearchParams.metadata_and = metadataBuckets.and;
  }
  if (metadataBuckets.or.length > 0) {
    spanSearchParams.metadata_or = metadataBuckets.or;
  }

  if (resourceBuckets.and.length > 0) {
    spanSearchParams.resource_and = resourceBuckets.and;
  }
  if (resourceBuckets.or.length > 0) {
    spanSearchParams.resource_or = resourceBuckets.or;
  }

  if (startTimeParam) {
    spanSearchParams.start_time = startTimeParam;
  }

  if (endTimeParam) {
    spanSearchParams.end_time = endTimeParam;
  }

  return useSpanFeed({
    userToken: userToken,
    searchParams: spanSearchParams,
    workspace_id: activeWorkspaceId!,
    parent_id: "none",
    enabledFlag,
  });
};

export const useNewRequests = () => {
  const user = useUser();
  const auth = useAuth();

  const userToken = auth?.userToken ?? "";
  const workspaceId = user?.activeWorkspaceId ?? 0;

  return useQuery<{ count_requests: number }>(
    [ENDPOINTS.new_requests, userToken, workspaceId],
    async () => {
      const url = new URL(`${API_URL}/workspaces/${workspaceId}/requests/new`);

      return fetch(url.toString(), {
        headers: authHeader(userToken),
        credentials: "include",
      }).then(handleResponse);
    },
    {
      refetchInterval: (data: { count_requests?: number } = {}) =>
        (data.count_requests ?? 0) > MAX_SIDEBAR_NEW_REQUESTS_THRESHOLD
          ? false
          : Number(import.meta.env.REACT_APP_REQUESTS_REFRESH_INTERVAL) || 5000,
    },
  );
};

export const useDeleteWorkflow = (userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ workflow_id }: { workflow_id: number }) =>
      fetch(`${ENDPOINTS.workflows}/${workflow_id}`, {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      }).then(handleResponse),
    {
      onSuccess: res => {
        if (!res.success) {
          console.error(res.message || res.error || "");
        }
        queryClient.invalidateQueries([ENDPOINTS.workflows]);
        queryClient.invalidateQueries([ENDPOINTS.workflow_versions]);
      },
      onError: error => {
        console.log("Error deleting workflow", error);
      },
    },
  );
};

export const useNewSpans = () => {
  const user = useUser();
  const auth = useAuth();

  const userToken = auth?.userToken ?? "";
  const workspaceId = user?.activeWorkspaceId ?? 0;

  return useQuery<{ count_spans: number }>(
    [ENDPOINTS.new_spans, userToken, workspaceId],
    async () => {
      const url = new URL(`${API_URL}/workspaces/${workspaceId}/spans/new`);
      return fetch(url.toString(), {
        headers: authHeader(userToken),
        credentials: "include",
      }).then(handleResponse);
    },
    {
      refetchInterval: (data: { count_spans?: number } = {}) =>
        (data.count_spans ?? 0) > MAX_SIDEBAR_NEW_REQUESTS_THRESHOLD
          ? false
          : Number(import.meta.env.REACT_APP_REQUESTS_REFRESH_INTERVAL) || 5000,
    },
  );
};

export const useReportRetry = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation(
    ({ reportId, statuses }: { reportId: number; statuses: CellStatus[] }) =>
      fetch(`${ENDPOINTS.legacy_reports}/${reportId}/retry`, {
        method: "POST",
        headers: authHeader(userToken),
        body: JSON.stringify({
          statuses,
        }),
      }).then(handleResponse),
    {
      onError: error => {
        displayErrorToast(error);
      },
      onSuccess: (data, variables) => {
        displayToast(data.message, ToastType.success);
        queryClient.invalidateQueries(["reportsForPrompt"]);
        // Invalidate the main report query to resume polling
        queryClient.invalidateQueries([ENDPOINTS.legacy_reports, variables.reportId]);
      },
    },
  );
};

export * from "./smart-tables";

export * from "./datasets";
export * from "./evals";
export * from "./folder-entities";
export * from "./input-variable-sets";
export * from "./playground";
export * from "./requests";
export * from "./slack";
export * from "./user";
export * from "./workspace";
